import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { NewsroomAdminNav } from "@/components/NewsroomAdminNav";
import { Button } from "@/components/ui/button";
import { usePageSeo } from "@/hooks/usePageSeo";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSponsorsPage = () => {
  usePageSeo({ canonicalPath: "/admin/sponsors", description: "Manage Content Watch HQ sponsors.", robots: "noindex,nofollow", title: "Sponsor Admin | Content Watch HQ" });
  const navigate = useNavigate();
  const [token] = useState(() => window.localStorage.getItem("content_watch_token"));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", website_url: "", phone: "", email: "", category: "", tier: "gold", promotional_offer: "", is_featured: true, is_active: true });

  const loadSponsors = async () => {
    const response = await axios.get(`${API_BASE}/office/sponsors`, { headers: { Authorization: `Bearer ${token}` } });
    setSponsors(response.data);
  };

  useEffect(() => {
    if (!token) {
      navigate("/newsroom-login");
      return;
    }
    axios.get(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).then(() => loadSponsors()).catch(() => {
      window.localStorage.removeItem("content_watch_token");
      navigate("/newsroom-login");
    }).finally(() => setLoading(false));
  }, [navigate, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => payload.append(key, `${value}`));
      if (logoFile) payload.append("logo", logoFile);
      await axios.post(`${API_BASE}/office/sponsors`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Sponsor saved.");
      setForm({ name: "", description: "", website_url: "", phone: "", email: "", category: "", tier: "gold", promotional_offer: "", is_featured: true, is_active: true });
      setLogoFile(null);
      await loadSponsors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sponsor could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16" data-testid="admin-sponsors-page">
      <NewsroomAdminNav />
      <div className="grid gap-8 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
        <form className="border border-[#D9DCE1] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] sm:p-8" data-testid="admin-sponsor-form" onSubmit={handleSubmit}>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#3BB143]">Sponsor manager</p>
          <h1 className="mt-3 font-[Bebas_Neue] text-4xl uppercase tracking-[0.04em] text-[#0A0A0A]">Save sponsor</h1>
          <p className="mt-3 text-sm leading-7 text-slate-700" data-testid="admin-sponsor-form-copy">Saving a real sponsor now replaces placeholder entries and updates an existing sponsor if you reuse the same sponsor name.</p>
          <div className="mt-8 grid gap-4">
            {Object.entries({ name: "Sponsor name", website_url: "Website URL", phone: "Phone number", email: "Email", category: "Category", description: "Description", promotional_offer: "Promotional offer" }).map(([key, label]) => (
              <input key={key} className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid={`admin-sponsor-input-${key}`} onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))} placeholder={label} required={key === "name"} value={form[key]} />
            ))}
            <select className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-sponsor-tier-select" onChange={(event) => setForm((current) => ({ ...current, tier: event.target.value }))} value={form.tier}>
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
              <option value="bronze">Bronze</option>
            </select>
            <input className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-sponsor-logo-input" onChange={(event) => setLogoFile(event.target.files?.[0] || null)} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" />
            <Button className="rounded-none bg-[#1338BE] px-5 py-6 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#102F9B]" data-testid="admin-sponsor-submit-button" disabled={saving} type="submit">
              {saving ? "Saving" : "Save Sponsor"}
            </Button>
          </div>
        </form>
        <section className="border border-[#D9DCE1] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] sm:p-8" data-testid="admin-sponsor-list-panel">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Current sponsors</p>
          {loading ? <p className="mt-4 text-sm text-slate-600">Loading sponsors…</p> : null}
          <div className="mt-8 space-y-5">
            {sponsors.map((sponsor) => (
              <article key={sponsor.id} className="border border-[#D9DCE1] bg-[#F8F9FA] p-5 sm:p-6" data-testid={`admin-sponsor-card-${sponsor.slug}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#1338BE]">{sponsor.tier} sponsor</p>
                    <h2 className="mt-2 text-lg font-bold uppercase tracking-[0.08em] text-[#0A0A0A]">{sponsor.name}</h2>
                  </div>
                  <p className="text-xs uppercase tracking-[0.16em] text-slate-600">CTR {sponsor.impressions ? Math.round((sponsor.clicks / sponsor.impressions) * 100) : 0}%</p>
                </div>
                <p className="mt-3 text-sm leading-7 text-slate-700">{sponsor.description}</p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
};